import { useState, useEffect } from "react";
import { Video, Plus, Calendar, Clock, Loader2, ExternalLink, Star, CheckCircle2 } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import PostCallRatingDialog from "@/components/PostCallRatingDialog";

interface VideoSession {
  id: string;
  host_id: string;
  participant_id: string;
  skill_id: string | null;
  meeting_link: string | null;
  scheduled_at: string | null;
  duration_minutes: number;
  status: string;
  session_record_id: string | null;
  ended_at: string | null;
}

interface SessionRecord {
  id: string;
  user1_id: string;
  user2_id: string;
  skill1_id: string | null;
  skill2_id: string | null;
  rating_from_user1: number | null;
  rating_from_user2: number | null;
  completed_at: string | null;
}

const VideoSessions = () => {
  const [sessions, setSessions] = useState<VideoSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<{
    sessionId: string;
    partnerId: string;
    partnerName: string;
    skillId?: string;
    isUser1: boolean;
  } | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) fetchSessions();
  }, [user]);

  const fetchSessions = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('video_sessions')
        .select('*')
        .or(`host_id.eq.${user.id},participant_id.eq.${user.id}`)
        .order('scheduled_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMeetingLink = () => {
    const roomId = Math.random().toString(36).substring(7);
    const link = `https://meet.jit.si/skillswipe-${roomId}`;
    setMeetingLink(link);
    
    toast({
      title: "Meeting link generated!",
      description: "You can now share this with your learning partner",
    });
  };

  const handleJoinMeeting = async (session: VideoSession) => {
    if (!user || !session.meeting_link) return;

    // Open the meeting in a new tab
    window.open(session.meeting_link, '_blank');

    // Check if there's a session record, if not create one
    if (!session.session_record_id) {
      const partnerId = session.host_id === user.id ? session.participant_id : session.host_id;
      
      // Create session record
      const { data: sessionRecord, error } = await supabase
        .from('sessions')
        .insert({
          user1_id: session.host_id,
          user2_id: session.participant_id,
          skill1_id: session.skill_id,
        })
        .select()
        .single();

      if (!error && sessionRecord) {
        // Update video session with session_record_id
        await supabase
          .from('video_sessions')
          .update({ session_record_id: sessionRecord.id })
          .eq('id', session.id);
      }
    }
  };

  const handleEndSession = async (session: VideoSession) => {
    if (!user) return;

    try {
      // Mark session as ended
      await supabase
        .from('video_sessions')
        .update({ 
          status: 'completed',
          ended_at: new Date().toISOString()
        })
        .eq('id', session.id);

      // Fetch partner info
      const partnerId = session.host_id === user.id ? session.participant_id : session.host_id;
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', partnerId)
        .single();

      // Get or create session record
      let sessionRecordId = session.session_record_id;
      if (!sessionRecordId) {
        const { data: newRecord } = await supabase
          .from('sessions')
          .insert({
            user1_id: session.host_id,
            user2_id: session.participant_id,
            skill1_id: session.skill_id,
          })
          .select()
          .single();
        
        if (newRecord) {
          sessionRecordId = newRecord.id;
          await supabase
            .from('video_sessions')
            .update({ session_record_id: newRecord.id })
            .eq('id', session.id);
        }
      }

      if (sessionRecordId) {
        const isUser1 = session.host_id === user.id;
        setSelectedSession({
          sessionId: sessionRecordId,
          partnerId,
          partnerName: partnerProfile?.name || 'Your Partner',
          skillId: session.skill_id || undefined,
          isUser1,
        });
        setRatingDialogOpen(true);
      }

      fetchSessions();
    } catch (error: any) {
      console.error('Error ending session:', error);
      toast({
        title: "Error",
        description: "Failed to end session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getSessionStatus = (session: VideoSession) => {
    if (session.ended_at || session.status === 'completed') {
      return { label: 'Completed', color: 'bg-green-500' };
    }
    if (session.status === 'in_progress') {
      return { label: 'In Progress', color: 'bg-blue-500' };
    }
    return { label: 'Scheduled', color: 'bg-orange-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="gradient-primary text-white p-6 pb-12 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Video className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Video Sessions</h1>
                <p className="text-sm text-white/80">Learn face-to-face, anywhere</p>
              </div>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="secondary" size="icon">
                  <Plus className="h-5 w-5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Start Video Session</DialogTitle>
                  <DialogDescription>
                    Generate a meeting link to share with your learning partner
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Meeting Link</Label>
                    {meetingLink ? (
                      <div className="flex gap-2 mt-2">
                        <Input value={meetingLink} readOnly />
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(meetingLink);
                            toast({
                              title: "Copied!",
                              description: "Link copied to clipboard"
                            });
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={generateMeetingLink}
                        className="w-full mt-2 gradient-primary text-white"
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Generate Meeting Link
                      </Button>
                    )}
                  </div>
                  {meetingLink && (
                    <div className="space-y-2">
                      <Button
                        onClick={() => window.open(meetingLink, '_blank')}
                        className="w-full gradient-primary text-white"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Join Meeting
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Share this link with your learning partner to start the session
                      </p>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="container max-w-md mx-auto px-4 -mt-6 space-y-4">
        <Card className="p-6 text-center shadow-soft gradient-primary text-white">
          <Video className="h-12 w-12 mx-auto mb-3" />
          <h3 className="font-bold text-lg mb-2">Start Learning Live!</h3>
          <p className="text-sm mb-4 text-white/90">
            Connect face-to-face with your skill partners through video calls. 
            Practice, teach, and learn in real-time.
          </p>
          <Button 
            onClick={() => setCreateOpen(true)}
            variant="secondary"
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Start New Session
          </Button>
        </Card>

        {/* How it works */}
        <Card className="p-4 shadow-soft">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            How Sessions Work
          </h3>
          <ol className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">1</span>
              Generate a meeting link and share with your partner
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">2</span>
              Join the video call and exchange skills
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">3</span>
              End session and rate your partner
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">4</span>
              Upload a screenshot to verify the skill exchange
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">5</span>
              Session completes when both users rate!
            </li>
          </ol>
        </Card>

        {sessions.length > 0 && (
          <>
            <div className="flex items-center gap-2 px-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-bold text-lg">Your Sessions</h2>
            </div>

            {sessions.map((session) => {
              const status = getSessionStatus(session);
              const isHost = session.host_id === user?.id;
              
              return (
                <Card key={session.id} className="p-5 shadow-soft">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <Badge className={`${status.color} text-white mb-2`}>
                        {status.label}
                      </Badge>
                      {session.scheduled_at && (
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(session.scheduled_at), 'PPp')}
                        </p>
                      )}
                    </div>
                    {isHost && <Badge variant="outline">Host</Badge>}
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Clock className="h-4 w-4" />
                    <span>{session.duration_minutes} minutes</span>
                  </div>

                  {session.meeting_link && status.label !== 'Completed' && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleJoinMeeting(session)}
                        className="flex-1 gradient-primary text-white"
                        size="sm"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Join Meeting
                      </Button>
                      <Button
                        onClick={() => handleEndSession(session)}
                        variant="outline"
                        size="sm"
                      >
                        <Star className="h-4 w-4 mr-2" />
                        End & Rate
                      </Button>
                    </div>
                  )}

                  {status.label === 'Completed' && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="text-sm">Session completed</span>
                    </div>
                  )}
                </Card>
              );
            })}
          </>
        )}
      </main>

      {/* Post-call rating dialog */}
      {selectedSession && (
        <PostCallRatingDialog
          open={ratingDialogOpen}
          onOpenChange={setRatingDialogOpen}
          sessionId={selectedSession.sessionId}
          partnerId={selectedSession.partnerId}
          partnerName={selectedSession.partnerName}
          skillId={selectedSession.skillId}
          isUser1={selectedSession.isUser1}
          onComplete={() => {
            setSelectedSession(null);
            fetchSessions();
          }}
        />
      )}

      <Navigation />
    </div>
  );
};

export default VideoSessions;